import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  COMMENT_IMAGE_ACCEPTED_TYPES,
  COMMENT_IMAGE_MAX_BYTES,
  COMMENT_IMAGE_MAX_FILES,
} from "../utils";

interface ImagePreview {
  name: string;
  url: string;
}

interface Params {
  pendingImages: File[];
  onImagesChange?: (files: File[]) => void;
  reservedCount: number;
}

export default function useCommentImagePicker({
  pendingImages,
  onImagesChange,
  reservedCount,
}: Params) {
  const { t } = useTranslation("common", { keyPrefix: "announcementPanel" });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const remainingSlots = COMMENT_IMAGE_MAX_FILES - reservedCount - pendingImages.length;

  const pendingRevokeRef = useRef<{
    previews: ImagePreview[];
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const previews = useMemo<ImagePreview[]>(
    () => pendingImages.map((file) => ({ name: file.name, url: URL.createObjectURL(file) })),
    [pendingImages],
  );

  useEffect(() => {
    if (pendingRevokeRef.current?.previews === previews) {
      clearTimeout(pendingRevokeRef.current.timer);
      pendingRevokeRef.current = null;
    }
    return () => {
      const timer = setTimeout(() => previews.forEach((p) => URL.revokeObjectURL(p.url)), 0);
      pendingRevokeRef.current = { previews, timer };
    };
  }, [previews]);

  const handleFilePick = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const files = Array.from(e.target.files ?? []);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (!files.length) return;

      const toAdd = files
        .filter((f) => COMMENT_IMAGE_ACCEPTED_TYPES.includes(f.type) && f.size <= COMMENT_IMAGE_MAX_BYTES)
        .slice(0, Math.max(remainingSlots, 0));
      if (toAdd.length < files.length) {
        setWarning(
          t("comments.imagesSkipped", {
            maxMb: COMMENT_IMAGE_MAX_BYTES / (1024 * 1024),
            maxFiles: COMMENT_IMAGE_MAX_FILES,
          }),
        );
      }
      if (toAdd.length > 0) onImagesChange?.([...pendingImages, ...toAdd]);
    },
    [pendingImages, onImagesChange, remainingSlots, t],
  );

  const removeImage = useCallback(
    (idx: number): void => {
      onImagesChange?.(pendingImages.filter((_, i) => i !== idx));
    },
    [pendingImages, onImagesChange],
  );

  const openFilePicker = useCallback(() => fileInputRef.current?.click(), []);
  const dismissWarning = useCallback(() => setWarning(null), []);

  return {
    fileInputRef,
    previews,
    handleFilePick,
    removeImage,
    openFilePicker,
    remainingSlots,
    warning,
    dismissWarning,
  };
}
